import * as http from "api/http";

import { SERVICES_URL, handleError } from "api/helper";
import { FacebookService } from "@adfenix/types";
export const Scopes: string[] = [
  "pages_show_list",
  "business_management",
  "pages_read_engagement",
  "pages_manage_posts",
  "public_profile",
  "instagram_basic",
  "instagram_content_publish",
  "pages_manage_metadata"
];
export const MandatoryScopes: string[] = [
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  "public_profile",
  "business_management"
];

export interface ConnectAgency {
  pageId: string;
  pageAccessToken: string;
  businessManagerId: string;
}

export interface FacebookIntegration {
  subjectId: string;
  subjectType: FacebookService.SubjectType;
  hqId: string;
  facebookPageId: string;
  facebookPageUrl?: string;
  storedFacebookPageUrl?: string;
  facebookPageName?: string;
  facebookProfileImage?: string;
  pixelId?: string;
  audienceId?: string;
  adAccountId?: string;
  offlineEventSetId?: string;
  lookalikeAudienceId?: string;
  instagramConfigured?: boolean;
  instagramActorId?: string;
  instagramPageName?: string;
  instagramProfileImage?: string;
  allowOfficeUsersToConfigureSettings: boolean;
  facebookUserName?: string;
  businessManagerId?: string;
  haveInstagramAccountInDb?: boolean;
}

export interface FacebookIntegrationOverview {
  subjectId: string;
  subjectType: FacebookService.SubjectType;
  facebookPageId: string;
  adAccountId?: string;
  instagramActorId?: string;
  facebookPageName?: string;
  instagramAccountName?: string;
  facebookUserName?: string;
  offlineEventSetId?: string;
}

export interface DeleteIntegrationRequest {
  subjectId: string;
  subjectType: FacebookService.SubjectType;
}

export interface PromotionStatusRequest {
  pageUrl: string;
  businessMangerId: string;
}

const baseUrl = "facebook/auth/v1";
const { get, post, put, del } = http;

export const getConnectedInstagramAccounts = (
  adAccountId: string,
  businessManagerId: string
): Promise<FacebookService.InstagramDetails[]> =>
  get(
    `${SERVICES_URL}/${baseUrl}/instagram-accounts/${adAccountId}/${businessManagerId}`
  ).catch(handleError);

export const getConnectedInstagramAccountsAttachedWithPage = (
  pageUrl: string,
  businessManagerId: string
): Promise<FacebookService.InstagramDetails[]> =>
  get(
    `${SERVICES_URL}/${baseUrl}/instagram-accounts-with-page/${pageUrl}/${businessManagerId}`
  ).catch(handleError);

export const getVerifiedFacebookAccount = (
  pageUrl: string,
  businessManagerId: string
): Promise<FacebookService.ProfileDetail> =>
  get(
    `${SERVICES_URL}/${baseUrl}/page-details/${pageUrl}/${businessManagerId}`
  ).catch(handleError);

export const connectAdfenixBusinessToCustomePage = (
  data: ConnectAgency
): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseUrl}/client-token?pageId=${data.pageId}&pageAccessToken=${data.pageAccessToken}&businessManagerId=${data.businessManagerId}`,
    {}
  ).catch(handleError);

export const getFacebookIntegrationConfig = (
  subjectId: string
): Promise<FacebookIntegration> =>
  get(`${SERVICES_URL}/${baseUrl}/facebook-integration/${subjectId}`).catch(
    handleError
  );

export const getFacebookIntegrationConfigBasedOnHqAndOffice = (
  hqId: string,
  officeId: string
): Promise<FacebookIntegration> =>
  get(
    `${SERVICES_URL}/${baseUrl}/facebook-integration/${hqId}/${officeId}`
  ).catch(handleError);

export const addFacebookIntegrationConfig = (
  data: FacebookIntegration
): Promise<string> =>
  post(`${SERVICES_URL}/${baseUrl}/facebook-integration`, data).catch(
    handleError
  );

export const updateFacebookIntegrationConfig = (
  data: FacebookIntegration
): Promise<string> =>
  put(`${SERVICES_URL}/${baseUrl}/facebook-integration`, data).catch(
    handleError
  );

export const updateInstagramIntegrationConfig = (
  data: FacebookService.InstagramIntegrationRequest
): Promise<string> =>
  put(`${SERVICES_URL}/${baseUrl}/facebook-integration/instagram`, data).catch(
    handleError
  );

export const deleteFacebookIntegration = (
  data: DeleteIntegrationRequest
): Promise<string> =>
  del(`${SERVICES_URL}/${baseUrl}/facebook-integration`, data).catch(
    handleError
  );

export const getOfficesFacebookIntegration = (
  hqId: string
): Promise<FacebookIntegrationOverview[]> =>
  get(`${SERVICES_URL}/${baseUrl}/facebook-integration/overview/${hqId}`).catch(
    handleError
  );

export const getBusinessManagers = (): Promise<
  FacebookService.BusinessManagerResponse[]
> =>
  get(
    `${SERVICES_URL}/${baseUrl}/facebook-integration/business-managers`
  ).catch(handleError);

export const getFacebookPagePromotionStatus = (
  data: PromotionStatusRequest
): Promise<FacebookService.PromotionEligibilityResponse> =>
  get(
    `${SERVICES_URL}/${baseUrl}/page/promotion-by-page/${data.pageUrl}/${data.businessMangerId}`
  ).catch(handleError);
